import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { TextField } from 'mui-rff';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Grid, Typography, Stack, Button } from '@mui/material';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import {
  Add as AddIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';

import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import { breadcrumbsActions } from '@store/breadcrumbs';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import FormSaveAndSubmit from '@components/FormSaveAndSubmit';
import Notification from '@components/Notification';
import MultiSelect, {
  REASON_CLEAR,
  REASON_REMOVE_OPTION,
  REASON_SELECT_OPTION,
} from '@components/MultiSelect';
import SelectedMerchantsTable from '../../components/SelectedMerchantsTable';

import {
  getCurrencies,
  selectCurrencies,
} from '../../store/payment-options-currencies';
import {
  getLOBDetails,
  getLOBs,
  selectLOBList,
} from '../../store/payment-options-merchants';
import {
  getPaymentMethods,
  selectMethodList,
} from '../../store/payment-options-methods';
import { createMaintenanceRule } from '../../store/payment-options-maintenance-rules';

import {
  addMaintenanceRules,
  MAINTENANCE_RULES_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';

import {
  PAGE_DETAILS,
  MAINTENANCE_RULE_DISPLAY_BEHAVIOR_LIST,
  MAINTENANCE_RULE_BANNER_TYPE_LIST,
  MAINTENANCE_RULE_DEFAULT_MESSAGE,
  MAINTENANCE_RULE_DEFAULT_BANNER_TYPE,
  MAINTENANCE_RULE_DEFAULT_DISPLAY_BEHAVIOR,
  MAINTENANCE_RULE_MAX_METHODS,
  MAINTENANCE_RULE_MAX_CURRENCIES,
} from '../../payment-options.constants';

const INITIAL_FORM = {
  // step1
  lob: null,
  merchants: null,

  // step2
  methodCode: [],

  // step3
  currency: [],

  // step4
  messageKey: '',
  message: MAINTENANCE_RULE_DEFAULT_MESSAGE,
  bannerType: MAINTENANCE_RULE_DEFAULT_BANNER_TYPE,
  displayBehavior: MAINTENANCE_RULE_DEFAULT_DISPLAY_BEHAVIOR,

  // step5
  startTime: dayjs(Date.now()).add(1, 'day').startOf('day'),
  endTime: dayjs(Date.now()).add(2, 'day').startOf('day'),
};

const FORM_VALIDATORS = {
  required: (value) =>
    value
      ? undefined
      : PAGE_DETAILS.MaintenanceRuleCreate.FormRequiredFieldError,
};

export default function MaintenanceRulesForm({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currencies = useSelector(selectCurrencies);
  const lobList = useSelector(selectLOBList);
  const methodList = useSelector(selectMethodList);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [formValue] = useState(INITIAL_FORM);
  const [addedMerchants, setAddedMerchants] = useState([]);
  const [showMerchantList, setShowMerchantList] = useState(false);
  const [showAddMethodBtn, setShowAddMethodBtn] = useState(false);
  const [selectedLOBMerchants, setSelectedLOBMerchants] = useState([]);
  const selectedMerchants = useRef({ lob: null, merchants: [] });
  const [openDialog, setOpenDialog] = useState({ show: false });

  // Permissions
  useEffect(() => {
    if (!permissions?.canCreateMaintenanceRule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  // Breadcrumbs
  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(addMaintenanceRules));
  }, [dispatch]);

  // Loading dropdown data
  useEffect(() => {
    setIsFetchingData(true);
    return Promise.all([
      dispatch(getCurrencies()),
      dispatch(getLOBs()),
      dispatch(getPaymentMethods()),
    ]).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  const handleRemoveMerchant = (lobId, setValue) => {
    setAddedMerchants((prev) => {
      const filteredMerchants = prev.filter(({ lob }) => lob.lobId !== lobId);
      if (!filteredMerchants || filteredMerchants.length === 0) {
        setValue('lob', null);
        setValue('merchants', []);
      }

      return filteredMerchants;
    });
  };

  const resetStep1 = useCallback(() => {
    selectedMerchants.current = { lob: null, merchants: [] };
    formValue.current = { ...formValue.current, lob: null, merchants: null };
    setShowMerchantList(false);
    setShowAddMethodBtn(false);
    setSelectedLOBMerchants([]);
  }, [selectedMerchants, formValue]);

  const handleOnChangeLOB = useCallback(
    (value, reason) => {
      if (reason === REASON_CLEAR || value === null) {
        resetStep1();
        return;
      }

      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowMerchantList(false);

        dispatch(getLOBDetails(value.lobId)).then(
          ({ payload: { merchantDetails } }) => {
            setSelectedLOBMerchants(merchantDetails);

            const lobExistsIdx = addedMerchants.findIndex(
              (merchant) => merchant.lob.lobId === value.lobId,
            );

            // reselect existing merchants
            selectedMerchants.current = {
              lob: value,
              merchants:
                lobExistsIdx > -1 ? addedMerchants[lobExistsIdx].merchants : [],
            };
            setShowMerchantList(true);
          },
        );
      }
    },
    [dispatch, resetStep1, selectedMerchants, addedMerchants],
  );

  const handleOnChangeMerchants = useCallback(
    (values, reason) => {
      if (reason === REASON_CLEAR) {
        setShowAddMethodBtn(false);
      }
      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowAddMethodBtn(values && values.length > 0);
      }
      selectedMerchants.current = {
        ...selectedMerchants.current,
        merchants: values,
      };
    },
    [selectedMerchants],
  );

  const handleAddMerchant = useCallback(() => {
    const newLobId = selectedMerchants.current.lob.lobId;
    const newMerchants = selectedMerchants.current.merchants;

    const existingIdx = addedMerchants.findIndex(
      (merchant) => merchant.lob.lobId === newLobId,
    );
    let updatedMerchants = addedMerchants.concat(selectedMerchants.current);

    if (existingIdx !== -1) {
      updatedMerchants = [...addedMerchants];
      updatedMerchants[existingIdx] = {
        ...selectedMerchants.current,
        merchants: newMerchants,
      };
    }
    setAddedMerchants(updatedMerchants);
    resetStep1();
  }, [selectedMerchants, addedMerchants, resetStep1]);

  const onStartTimeChangeHandler = (newValue, values, onChange, setValue) => {
    onChange(newValue);

    if (dayjs(values.endTime).isBefore(newValue)) {
      setValue('endTime', dayjs(newValue).add(1, 'day').startOf('day'));
    }
  };

  const onSubmit = ({
    currency,
    methodCode,
    startTime,
    endTime,
    message,
    messageKey,
    bannerType,
    displayBehavior,
  }) => {
    const merchantIds = addedMerchants.reduce((prev, { merchants }) => {
      const lobMerchants = merchants.map((merchant) => merchant.merchantId);
      return prev.concat(lobMerchants);
    }, []);

    const currencyIds = currency.map(({ id }) => id);
    const methodIds = methodCode.map(({ methodId }) => methodId);

    const payload = {
      merchantIds,
      currencyIds,
      methodIds,
      startTime,
      endTime,
      message,
      messageKey,
      messageBannerType: bannerType?.code,
      displayBehavior: displayBehavior?.code,
    };

    dispatch(createMaintenanceRule(payload)).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        showSuccessMessage(PAGE_DETAILS.MaintenanceRuleCreate.CreateSuccess);
        navigate(MAINTENANCE_RULES_LIST_ROUTE);
      } else {
        showErrorMessage(PAGE_DETAILS.MaintenanceRuleCreate.CreateFailure);
      }
    });
  };

  const renderFormActions = ({
    pristine,
    submitting,
    invalid,
    form,
    permissions: userPermissions,
    isMerchantAdded,
  }) => (
    <Grid
      item
      xs={12}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 3,
      }}>
      <FormSaveAndSubmit
        resetDisabled={pristine}
        submitDisabled={
          !userPermissions.canCreateMaintenanceRule ||
          submitting ||
          pristine ||
          invalid ||
          !isMerchantAdded
        }
        handleReset={() => {
          form.reset();
        }}
      />
    </Grid>
  );

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate pageReady={!isFetchingData}>
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...formValue }}
        mutators={{
          ...arrayMutators,
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        render={({
          handleSubmit,
          values,
          pristine,
          submitting,
          invalid,
          form,
          form: {
            mutators: { setValue },
          },
        }) => (
          <form
            onSubmit={(e) =>
              process.env.REACT_APP_ENV !== 'production'
                ? handleSubmit(e)
                : handlePreSubmit(e)
            }
            noValidate>
            <ProdConfirmationDialog
              openDialog={openDialog}
              yesHandler={handleSubmit}
            />
            <Grid
              container
              spacing={1}
              mb={2}>
              {renderFormActions({
                form,
                submitting,
                pristine,
                invalid,
                values,
                permissions,
                isMerchantAdded: addedMerchants && addedMerchants.length !== 0,
              })}
            </Grid>
            <Stack gap={4}>
              <Stack gap={2}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Step 1
                </Typography>
                {addedMerchants && addedMerchants.length > 0 && (
                  <SelectedMerchantsTable
                    merchants={addedMerchants}
                    handleRemoveMerchant={(lobId) =>
                      handleRemoveMerchant(lobId, setValue)
                    }
                  />
                )}

                <Grid
                  container
                  sx={{ marginTop: '0' }}
                  spacing={2}>
                  <Grid
                    item
                    xs={3}>
                    <Field name="lob">
                      {({ input: { name, value } }) => (
                        <MultiSelect
                          {...{ name, value }}
                          items={lobList}
                          value={selectedMerchants.current.lob}
                          isOptionEqualToValue={(option, newValue) =>
                            option.lobId === newValue.lobId
                          }
                          multiple={false}
                          label={PAGE_DETAILS.MaintenanceRuleCreate.LobField}
                          optionDisplayKey="lobName"
                          onChange={handleOnChangeLOB}
                        />
                      )}
                    </Field>
                  </Grid>
                  {showMerchantList && (
                    <Grid
                      item
                      xs={9}
                      md={5}>
                      <Field
                        name="merchants"
                        validate={FORM_VALIDATORS.required}>
                        {({ input: { name, value } }) => (
                          <MultiSelect
                            {...{ name, value }}
                            label={
                              PAGE_DETAILS.MaintenanceRuleCreate.PlatformField
                            }
                            items={selectedLOBMerchants}
                            value={selectedMerchants.current.merchants}
                            isOptionEqualToValue={(option, newValue) =>
                              option.merchantId === newValue.merchantId
                            }
                            optionDisplayKey="platformName"
                            onChange={handleOnChangeMerchants}
                          />
                        )}
                      </Field>
                    </Grid>
                  )}
                  {showAddMethodBtn && (
                    <Grid
                      item
                      xs={12}
                      md={4}>
                      <Button
                        fullWidth
                        size="large"
                        startIcon={<RestartAltIcon />}
                        variant="outlined"
                        type="button"
                        onClick={resetStep1}>
                        Reset
                      </Button>
                      <Button
                        fullWidth
                        size="large"
                        startIcon={<AddIcon />}
                        variant="contained"
                        type="button"
                        onClick={handleAddMerchant}>
                        Add Merchant
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Stack>
              <Stack gap={2}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Step 2
                </Typography>
                <Field
                  name="methodCode"
                  validate={FORM_VALIDATORS.required}>
                  {({ input: { name, value, onChange } }) => (
                    <MultiSelect
                      {...{ name, value, onChange }}
                      required
                      disableSelectAll
                      label={PAGE_DETAILS.MaintenanceRuleCreate.MethodCodeField}
                      items={methodList}
                      helperText={`Maximum ${MAINTENANCE_RULE_MAX_METHODS} methods`}
                      optionDisplayKey="methodCode"
                      customOptionLabel={(option) =>
                        `${option.methodName} (${option.methodCode})`
                      }
                      isOptionEqualToValue={(option, newValue) =>
                        option.methodCode === newValue.methodCode
                      }
                      getOptionDisabled={(option) =>
                        value.length >= MAINTENANCE_RULE_MAX_METHODS &&
                        !value.some(
                          ({ methodCode }) => methodCode === option.methodCode,
                        )
                      }
                    />
                  )}
                </Field>
              </Stack>
              <Stack gap={2}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Step 3
                </Typography>
                <Field
                  name="currency"
                  validate={FORM_VALIDATORS.required}>
                  {({ input: { name, value, onChange } }) => (
                    <MultiSelect
                      {...{ name, value, onChange }}
                      required
                      disableSelectAll
                      label={PAGE_DETAILS.MaintenanceRuleCreate.CurrencyField}
                      items={currencies}
                      helperText={`Maximum ${MAINTENANCE_RULE_MAX_CURRENCIES} currency`}
                      optionDisplayKey="code"
                      customOptionLabel={(option) =>
                        `${option.name} (${option.code})`
                      }
                      isOptionEqualToValue={(option, newValue) =>
                        option.code === newValue.code
                      }
                      getOptionDisabled={(option) =>
                        value.length >= MAINTENANCE_RULE_MAX_CURRENCIES &&
                        !value.some(({ code }) => code === option.code)
                      }
                    />
                  )}
                </Field>
              </Stack>
              <Stack gap={2}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Step 4
                </Typography>
                <Grid
                  container
                  spacing={4}>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Field name="messageKey">
                      {({ input: { name, value, onChange } }) => (
                        <TextField
                          {...{ name, value, onChange }}
                          label={
                            PAGE_DETAILS.MaintenanceRuleCreate.MessageKeyField
                          }
                          type="text"
                          InputProps={{
                            inputProps: { min: 0 },
                          }}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Field
                      name="message"
                      validate={FORM_VALIDATORS.required}>
                      {({ input: { name, value, onChange, onBlur } }) => (
                        <TextField
                          {...{ name, value, onChange }}
                          required
                          label={
                            PAGE_DETAILS.MaintenanceRuleCreate.MessageField
                          }
                          type="text"
                          // restore the default when the field is left empty
                          onBlur={(event) => {
                            onBlur(event);

                            if (!value) {
                              onChange(MAINTENANCE_RULE_DEFAULT_MESSAGE);
                            }
                          }}
                          InputProps={{
                            inputProps: { min: 0 },
                          }}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Field
                      name="bannerType"
                      validate={FORM_VALIDATORS.required}>
                      {({ input: { name, value, onChange } }) => (
                        <MultiSelect
                          {...{ name, value, onChange }}
                          required
                          label={
                            PAGE_DETAILS.MaintenanceRuleCreate.BannerTypeField
                          }
                          items={MAINTENANCE_RULE_BANNER_TYPE_LIST}
                          multiple={false}
                          optionDisplayKey="code"
                          customOptionLabel={(option) =>
                            `${option.name} (${option.code})`
                          }
                          isOptionEqualToValue={(option, newValue) =>
                            option.code === newValue.code
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Field
                      name="displayBehavior"
                      validate={FORM_VALIDATORS.required}>
                      {({ input: { name, value, onChange } }) => (
                        <MultiSelect
                          {...{ name, value, onChange }}
                          required
                          label={
                            PAGE_DETAILS.MaintenanceRuleCreate
                              .DisplayBehaviorField
                          }
                          items={MAINTENANCE_RULE_DISPLAY_BEHAVIOR_LIST}
                          multiple={false}
                          optionDisplayKey="code"
                          customOptionLabel={(option) =>
                            `${option.name} (${option.code})`
                          }
                          isOptionEqualToValue={(option, newValue) =>
                            option.code === newValue.code
                          }
                        />
                      )}
                    </Field>
                  </Grid>

                  {/* Notification Preview */}
                  {values?.bannerType?.code && (
                    <Grid
                      item
                      xs={12}>
                      <Stack gap={2}>
                        <Typography variant="body1">Preview</Typography>
                        <Stack gap={2}>
                          <Notification
                            type={values?.bannerType?.code}
                            msg={values.message}
                            msgKey={values.messageKey}
                          />
                        </Stack>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Stack>
              <Stack gap={2}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Step 5
                </Typography>
                <Grid
                  container
                  spacing={4}>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Field
                      name="startTime"
                      validate={FORM_VALIDATORS.required}>
                      {({
                        input: { name, value, onChange },
                        meta: { error },
                      }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            required
                            {...{ name, value }}
                            onChange={(newValue) => {
                              if (!newValue) return;

                              onStartTimeChangeHandler(
                                newValue,
                                values,
                                onChange,
                                setValue,
                              );
                            }}
                            label={
                              PAGE_DETAILS.MaintenanceRuleCreate.StartTimeField
                            }
                            minDate={dayjs(Date.now()).add(1, 'day')}
                            slotProps={{
                              field: { fullWidth: true },
                              textField: {
                                helperText: error,
                                error: !!error,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    </Field>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Field
                      name="endTime"
                      validate={FORM_VALIDATORS.required}>
                      {({
                        input: { name, value, onChange },
                        meta: { error },
                      }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            required
                            {...{ name, value }}
                            onChange={(newValue) => {
                              if (!newValue) return;

                              onChange(newValue);
                            }}
                            label={
                              PAGE_DETAILS.MaintenanceRuleCreate.EndTimeField
                            }
                            minDate={dayjs(values.startTime)}
                            slotProps={{
                              field: { fullWidth: true },
                              textField: {
                                helperText: error,
                                error: !!error,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    </Field>
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </form>
        )}
      />
    </ApplicationPageTemplate>
  );
}
